import { z } from 'zod';

/**
 * The one shape every non-2xx response takes - docs/adr/0030-control-api-http-contract.md.
 *
 * The schema is the source of truth and the type is inferred from it. Elsewhere
 * in this package a shape is a hand-written interface, because nothing validates
 * it at runtime; a wire shape is checked on arrival, and two definitions of the
 * same thing drift the first time only one of them is edited.
 */
export const ERROR_CODES = [
  /** A request body, query or path parameter failed its schema. Carries `details`. */
  'VALIDATION_FAILED',
  /** No token, or a token the gateway refused. */
  'UNAUTHENTICATED',
  /** A valid token whose subject has no `app_user` row yet - AUTH-010 creates it. */
  'USER_NOT_PROVISIONED',
  /** A workspace-scoped route was called without the `x-workspace-id` header. */
  'WORKSPACE_REQUIRED',
  /** The caller is not an active member of the workspace they named. */
  'WORKSPACE_FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  /** Anything unexpected. The cause goes to the log, never to the client. */
  'INTERNAL',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const apiErrorDetailSchema = z
  .object({
    /** Dotted path into the request body, or the parameter name. */
    path: z.string(),
    message: z.string(),
  })
  .readonly();

export const apiErrorBodySchema = z
  .object({
    error: z
      .object({
        code: z.enum(ERROR_CODES),
        /** For a human reading a log or a toast. Clients switch on `code`. */
        message: z.string(),
        /** Present only for VALIDATION_FAILED. */
        details: z.array(apiErrorDetailSchema).readonly().optional(),
        /** Always present. ADR-025 makes a trace id load-bearing everywhere. */
        traceId: z.string(),
      })
      .readonly(),
  })
  .readonly();

export type ApiErrorDetail = z.infer<typeof apiErrorDetailSchema>;
export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>;
